import { BgColorsOutlined } from '@ant-design/icons';
import { Dropdown } from 'antd';
import { useTranslation } from 'react-i18next';
import { themeOptions, useTheme } from '../app/theme';
import { DEFAULT_THEME_ID } from '../theme/themes';
import { useActiveTheme, useChooseMyTheme } from '../api/themes';

/** The menu key that clears a person's own choice; a theme code can never look like this. */
const FOLLOW_DEFAULT = '__follow-default__';

/**
 * The theme picker, beside the language toggle. Both are the same kind of choice — how the person wants to read the
 * screen — so they sit together rather than being buried in settings.
 *
 * A theme's name is its own, not a translated string: "ADPAD Navy" is what an administrator called it, and translating
 * a name someone chose would make the two lists disagree.
 *
 * A person wearing their own choice is offered the way back: "follow the department default". Without it, a choice
 * once made was for ever — the default set on admin/themes could never reach them again.
 */
export function ThemeSwitch() {
  const { t } = useTranslation();
  const id = useTheme((s) => s.id);
  const choose = useTheme((s) => s.choose);
  const saved = useTheme((s) => s.saved);
  const active = useActiveTheme().data;
  const remember = useChooseMyTheme();
  // the three that ship, then whatever the department built
  const options = [...themeOptions, ...saved.map((s2) => ({ id: s2.code, name: s2.name, dark: s2.theme.dark }))];
  const defaultCode = active?.defaultCode ?? DEFAULT_THEME_ID;
  const defaultName = options.find((o) => o.id === defaultCode)?.name ?? defaultCode;

  return (
    <Dropdown
      trigger={['click']}
      menu={{
        selectable: true,
        selectedKeys: [id],
        items: [
          ...options.map((o) => ({ key: o.id, label: o.name })),
          ...(active?.chosen ? [{ type: 'divider' as const }, { key: FOLLOW_DEFAULT, label: t('topbar.theme.followDefault', { name: defaultName }) }] : []),
        ],
        onClick: ({ key }) => {
          if (key === FOLLOW_DEFAULT) {
            // clearing the choice: the server's next answer is the default, and useServerTheme wears it
            remember.mutate(null);
            return;
          }
          choose(key);
          // remembered against the account as well as the browser, so it follows the person to another machine
          remember.mutate(key);
        },
      }}
    >
      <button type="button" id="themeBtn" className="lang" title={t('topbar.theme.choose')} aria-label={t('topbar.theme.choose')}>
        <BgColorsOutlined aria-hidden="true" />
      </button>
    </Dropdown>
  );
}
